import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Volume2, Check, X, Flag, RotateCcw } from "lucide-react";
import { childApi, apiError } from "../lib/api";
import { playAudioSpec, playChime, installAudioUnlock } from "../lib/audio";
import { StaveView } from "../components/Stave";
import { AudioGate, SessionSummary, FeedbackPanel } from "../components/SessionParts";

const AUDIO_FAIL_MSG = "We couldn't play the sound. Check your device volume and try again.";

export default function SessionPlayer() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [sessionData] = useState(state?.sessionData || null);
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [summary, setSummary] = useState(null);
  const [pendingSummary, setPendingSummary] = useState(null);
  const [flagged, setFlagged] = useState(false);
  const [error, setError] = useState("");
  const [audioGate, setAudioGate] = useState(true); // "Can you hear this note?" check
  const [audioPlayed, setAudioPlayed] = useState(false);
  const [audioError, setAudioError] = useState("");
  const [audioBusy, setAudioBusy] = useState(false);
  const [taps, setTaps] = useState([]);
  const busyRef = useRef(false);
  const [singPhase, setSingPhase] = useState("study"); // study -> sing -> review (unseen sight-singing)
  const [studyLeft, setStudyLeft] = useState(null);
  const [modelPlayed, setModelPlayed] = useState(false);

  useEffect(() => { if (!sessionData) navigate("/play/home"); }, [sessionData, navigate]);
  useEffect(() => { installAudioUnlock(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (studyLeft === null) return undefined;
    if (studyLeft <= 0) { setSingPhase("sing"); return undefined; }
    const t = setTimeout(() => setStudyLeft(studyLeft - 1), 1000);
    return () => clearTimeout(t);
  }, [studyLeft]);

  if (!sessionData) return null;

  const questions = sessionData.questions;
  const question = questions[index];
  const isTest = sessionData.kind === "test" || sessionData.kind === "finale";
  const hasAudio = questions.some((q) => q.audio);
  const needsGate = hasAudio && audioGate;
  const isTap = question?.type === "tap";
  const isUnseen = question?.type === "sing_unseen";

  const hear = async (spec) => {
    setAudioBusy(true);
    setAudioError("");
    try {
      await playAudioSpec(spec || question.audio);
      setAudioPlayed(true);
    } catch (err) {
      console.error("[ChoirQuest audio] playback failed:", err);
      setAudioError(AUDIO_FAIL_MSG);
    } finally {
      setAudioBusy(false);
    }
  };

  const choose = async (optIdx) => {
    if (busyRef.current || feedback) return;
    busyRef.current = true;
    setSelected(optIdx);
    setError("");
    try {
      const { data } = await childApi.post("/child/answer", {
        attempt_id: sessionData.attempt_id, question_id: question.id, answer: optIdx,
      });
      if (!isTest) playChime(data.correct).catch(() => {});
      setFeedback(data);
      if (data.finished) setPendingSummary(data.summary);
    } catch (e) {
      if (e?.response?.status === 409) {
        // safety net: this question was already recorded — never trap the learner
        setFeedback({ duplicate: true, explanation: "That one was already recorded — carry on!", notes_awarded: 0 });
      } else {
        setError(apiError(e));
        setSelected(null);
      }
    } finally {
      busyRef.current = false;
    }
  };

  const submitTaps = async (tapTimes) => {
    if (busyRef.current || feedback) return;
    busyRef.current = true;
    setError("");
    try {
      const { data } = await childApi.post("/child/answer", {
        attempt_id: sessionData.attempt_id, question_id: question.id, answer: { taps: tapTimes },
      });
      playChime(data.correct).catch(() => {});
      setFeedback(data);
      if (data.finished) setPendingSummary(data.summary);
    } catch (e) {
      setError(apiError(e));
    } finally {
      busyRef.current = false;
    }
  };

  const onTap = () => {
    if (feedback) return;
    const now = performance.now() / 1000;
    const expected = (question.audio?.pattern || []).length;
    const next = [...taps, now];
    setTaps(next);
    if (next.length >= expected) {
      const t0 = next[0];
      submitTaps(next.map((t) => +(t - t0).toFixed(3)));
    }
  };

  const next = () => {
    if (pendingSummary) {
      setSummary(pendingSummary);
      return;
    }
    if (index + 1 >= questions.length) {
      childApi.post("/child/finish", { attempt_id: sessionData.attempt_id })
        .then(({ data }) => setSummary(data.summary));
      return;
    }
    setIndex(index + 1);
    setSelected(null); setFeedback(null); setFlagged(false);
    setAudioPlayed(false); setAudioError(""); setTaps([]);
    setSingPhase("study"); setStudyLeft(null); setModelPlayed(false);
  };

  const flag = async () => {
    try { await childApi.post("/child/flag-question", { question_id: question.id }); setFlagged(true); } catch (err) { console.error("Flag failed:", err); }
  };

  if (summary) {
    return <SessionSummary summary={summary} isTest={isTest} onHome={() => navigate("/play/home")} />;
  }

  if (needsGate) {
    return (
      <AudioGate audioBusy={audioBusy} audioPlayed={audioPlayed} audioError={audioError}
        onListen={() => hear({ type: "interval", base: 60, semitones: 0, mode: "melodic" })}
        onContinue={() => setAudioGate(false)} onSkip={() => setAudioGate(false)} />
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7EE] flex flex-col" data-testid="session-player">
      <header className="bg-[#0B192C] text-[#FAF7EE] px-5 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <span className="font-display font-bold" data-testid="session-title">{sessionData.topic}</span>
          <span className="text-sm font-semibold text-[#C69B3D]" data-testid="session-progress">{index + 1} of {questions.length}</span>
        </div>
        <div className="max-w-2xl mx-auto h-1.5 rounded-full bg-[#1E3A5F] mt-3 overflow-hidden">
          <div className="h-full bg-[#C69B3D] transition-all" style={{ width: `${(index / questions.length) * 100}%` }} />
        </div>
      </header>

      <main key={question.id} className="flex-1 w-full max-w-2xl mx-auto px-5 py-6">
        <p className="text-xs font-bold uppercase tracking-widest text-[#566270]">
          {isTest ? "Test — no feedback until the end" : sessionData.kind === "daily" ? "Daily Notes" : "Challenge"}
        </p>
        <h1 className="font-display text-xl sm:text-2xl font-bold text-[#0B192C] mt-2 whitespace-pre-line" data-testid="question-text">
          {question.text}
        </h1>
        {question.image && (
          <figure className="mt-3" data-testid="question-image">
            <img src={question.image} alt="Real printed music excerpt" className="w-full max-w-lg rounded-xl border-2 border-[#E5DEC9] bg-white" loading="lazy" />
          </figure>
        )}

        {question.notation && (
          <div className="mt-4">
            <StaveView notation={question.notation} width={Math.min(620, window.innerWidth - 64)} />
          </div>
        )}

        {question.audio && !isTap && !isUnseen && (
          <div className="mt-4 flex gap-3 flex-wrap">
            <button onClick={() => hear()} disabled={audioBusy} data-testid="hear-audio-btn"
              className="inline-flex items-center gap-2 px-5 py-3.5 rounded-2xl bg-[#0B192C] text-[#FAF7EE] font-bold hover:bg-[#12253E] transition-colors min-h-[52px] disabled:opacity-50">
              <Volume2 className="w-5 h-5 text-[#C69B3D]" aria-hidden /> {audioPlayed ? "↻ Play again" : "▶ Listen"}
            </button>
          </div>
        )}
        {audioError && <p role="alert" className="mt-3 text-sm text-[#A62B2B]" data-testid="audio-error">{audioError}</p>}

        {isUnseen && (
          <div className="mt-4 rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 space-y-4" data-testid="sing-unseen">
            {singPhase === "study" && (
              <>
                <p className="text-sm font-bold text-[#0B192C]">1 · Play your starting note — nothing else plays yet</p>
                <button onClick={() => hear(question.audio)} disabled={audioBusy} data-testid="starting-note-btn"
                  className="inline-flex items-center gap-2 px-5 py-3.5 rounded-2xl bg-[#0B192C] text-[#FAF7EE] font-bold min-h-[52px] disabled:opacity-50">
                  <Volume2 className="w-5 h-5 text-[#C69B3D]" aria-hidden /> ▶ Play my starting note
                </button>
                <p className="text-sm font-bold text-[#0B192C] pt-2">2 · Study the music ({question.study_secs || 20} seconds)</p>
                {studyLeft === null ? (
                  <button onClick={() => setStudyLeft(question.study_secs || 20)} data-testid="start-study-btn"
                    className="inline-flex items-center gap-2 px-5 py-3.5 rounded-2xl border-2 border-[#0B192C] text-[#0B192C] font-bold min-h-[52px]">
                    Start the study timer
                  </button>
                ) : (
                  <p className="font-display text-3xl font-bold text-[#7A1C2E]" data-testid="study-countdown">{studyLeft}s — read the shapes and rhythms</p>
                )}
                <button onClick={() => { setStudyLeft(null); setSingPhase("sing"); }} data-testid="skip-study-btn"
                  className="text-xs text-[#566270] underline min-h-[44px]">I'm ready now — skip the timer</button>
              </>
            )}
            {singPhase === "sing" && (
              <>
                <p className="font-display text-xl font-bold text-[#0B192C]">Your turn — sing the whole line on 'la'!</p>
                <button onClick={() => setSingPhase("review")} data-testid="sung-it-btn"
                  className="w-full px-6 py-4 rounded-2xl bg-[#C69B3D] text-[#0B192C] font-bold hover:bg-[#D8AB49] transition-colors min-h-[56px]">
                  I've sung it — show me the correct version
                </button>
              </>
            )}
            {singPhase === "review" && (
              <>
                <p className="text-sm font-bold text-[#0B192C]">3 · Now listen to the correct version and mark yourself honestly</p>
                <button onClick={async () => { await hear(question.model_audio); setModelPlayed(true); }} disabled={audioBusy}
                  data-testid="hear-model-btn"
                  className="inline-flex items-center gap-2 px-5 py-3.5 rounded-2xl bg-[#7A1C2E] text-[#FAF7EE] font-bold min-h-[52px] disabled:opacity-50">
                  <Volume2 className="w-5 h-5" aria-hidden /> {modelPlayed ? "↻ Hear the correct version again" : "▶ Hear the correct version"}
                </button>
                {!modelPlayed && <p className="text-xs text-[#566270]">Listen to the correct version first, then answer below.</p>}
              </>
            )}
          </div>
        )}

        {isTap && (
          <div className="mt-6 space-y-4">
            <button onClick={() => hear()} disabled={audioBusy} data-testid="hear-pattern-btn"
              className="inline-flex items-center gap-2 px-5 py-3.5 rounded-2xl bg-[#0B192C] text-[#FAF7EE] font-bold min-h-[52px] disabled:opacity-50">
              <Volume2 className="w-5 h-5 text-[#C69B3D]" aria-hidden /> {audioPlayed ? "↻ Play again" : "▶ Hear the pattern"}
            </button>
            <button onClick={onTap} disabled={!!feedback} data-testid="tap-pad"
              className="w-full py-10 rounded-3xl bg-[#C69B3D] text-[#0B192C] font-display text-xl font-bold hover:bg-[#D8AB49] active:bg-[#B8860B] transition-colors disabled:opacity-60 select-none">
              {feedback ? "Tapped!" : taps.length ? `Tap… (${taps.length}/${(question.audio?.pattern || []).length})` : "Tap here in rhythm"}
            </button>
            {taps.length > 0 && !feedback && (
              <button onClick={() => setTaps([])} data-testid="tap-reset" className="text-xs text-[#566270] underline min-h-[44px]">
                Start my taps again
              </button>
            )}
          </div>
        )}

        {!isTap && (
          <div className="mt-6 grid gap-3 sm:grid-cols-2" role="listbox" aria-label="Answer options">
            {question.options.map((opt, i) => {
              let cls = "border-[#E5DEC9] bg-white text-[#0B192C] hover:border-[#C69B3D]";
              if (feedback && selected === i) {
                cls = feedback.correct ? "border-[#2D6A4F] bg-[#EBF6F0] text-[#2D6A4F]" : "border-[#A62B2B] bg-[#FBF0F2] text-[#A62B2B]";
              } else if (feedback && feedback.correct_answer === i && !isTest) {
                cls = "border-[#2D6A4F] bg-[#EBF6F0] text-[#2D6A4F]";
              } else if (selected === i) {
                cls = "border-[#C69B3D] bg-[#F6EED8] text-[#0B192C]";
              }
              return (
                <button key={`${question.id}-${i}`} onClick={() => choose(i)} disabled={!!feedback || (isUnseen && (singPhase !== "review" || !modelPlayed))}
                  data-testid={`answer-option-${i}`} role="option" aria-selected={selected === i}
                  className={`text-left px-5 py-4 rounded-2xl border-2 font-semibold transition-colors min-h-[56px] flex items-center gap-3 ${cls} disabled:cursor-default`}>
                  {feedback && selected === i && (feedback.correct
                    ? <Check className="w-5 h-5 shrink-0" aria-hidden /> : <X className="w-5 h-5 shrink-0" aria-hidden />)}
                  {opt}
                </button>
              );
            })}
          </div>
        )}

        {error && <p role="alert" className="mt-4 text-sm text-[#A62B2B]" data-testid="answer-error">{error}</p>}

        {feedback && !isTest && <FeedbackPanel feedback={feedback} />}
        {feedback && isTest && (
          <p className="mt-5 text-sm text-[#566270]" data-testid="test-recorded">Answer recorded — results at the end.</p>
        )}

        <div className="mt-6 flex items-center justify-between gap-3">
          <button onClick={flag} disabled={flagged} data-testid="flag-question-btn"
            className="inline-flex items-center gap-1.5 text-xs text-[#566270] hover:text-[#7A1C2E] min-h-[44px] px-2 disabled:opacity-50">
            <Flag className="w-3.5 h-3.5" aria-hidden /> {flagged ? "Thanks — reported" : "Something looks wrong"}
          </button>
          {feedback && (
            <button onClick={next} data-testid="next-question-btn"
              className="inline-flex items-center gap-1.5 px-6 py-3.5 rounded-2xl bg-[#0B192C] text-[#FAF7EE] font-bold hover:bg-[#12253E] transition-colors min-h-[52px]">
              {index + 1 >= questions.length ? "See results" : "Next question"} <RotateCcw className="w-4 h-4 hidden" aria-hidden />
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
